import React, { useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, Button, Snackbar, Alert } from '@mui/material';
import {
  AdminPanelSettings as ConsoleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import SuperAdminConsole from '../pages/SuperAdminConsole';
import { DEGRADE_CONSOLE_SA } from '../theme/jetons';

/**
 * Châssis de la console super-admin (D-UI-1).
 *
 * Châssis SÉPARÉ, et non la Sidebar métier : le SUPER_ADMIN n'a ni entreprise ni
 * stock, donc le sélecteur de stock, le compteur de notifications (scopé par stock)
 * et l'entrée Admin n'ont aucun sens pour lui. D-UI-2 : il n'accède pas à /admin —
 * déjà appliqué de deux façons (Sidebar.js teste `role === 'ADMIN'` en égalité
 * stricte, et Admin.js rejette tout rôle autre qu'ADMIN).
 *
 * DÉCONNEXION : `onLogout` est le handler `handleLogout` de App.js — EXACTEMENT
 * celui que reçoit la Sidebar métier (performLogout + remise à zéro du state React).
 * On ne réimplémente pas le chemin. À ne pas confondre avec celui de Header.js, qui
 * fait `performLogout()` puis un `window.location.href = '/'` (rechargement complet).
 *
 * ROUTAGE : toute URL autre que /super-admin redirige vers /super-admin. Le SA n'a
 * aucune autre page atteignable — sans cette redirection, /products lui afficherait
 * une zone vide sous une barre fonctionnelle, ce qui ressemblerait à un bug.
 */
const SuperAdminShell = ({ user, onLogout }) => {
  const { t } = useLanguage();

  // Snackbar LOCAL, et non celui de App.js. Ce dernier est rendu dans le return
  // principal d'AppContent, donc APRÈS l'early return qui monte cette console :
  // appeler son showSnackbar d'ici changerait son state sans jamais rien afficher.
  // (Le même défaut touche le cul-de-sac « aucun stock » — noté au ROADMAP.)
  // Partager celui d'App.js imposerait d'envelopper AppContent pour sortir le JSX
  // au-dessus des early returns : de la chirurgie sur un fichier central pour un
  // bénéfice marginal.
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
      <AppBar
        position="fixed"
        // Dégradé neutre, volontairement distinct de celui du châssis métier : ce
        // dernier se colore avec `currentStock.color`, notion qui n'existe pas ici.
        sx={{ background: DEGRADE_CONSOLE_SA }}
      >
        <Toolbar>
          <ConsoleIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" component="div" noWrap>
            {t('superAdmin.consoleTitle')}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="body2" sx={{ mr: 1, opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
            {user?.full_name || user?.email}
          </Typography>

          <LanguageSwitcher />

          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout} sx={{ ml: 1 }}>
            {t('navigation.logout')}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Toolbar fantôme : décale le contenu sous l'AppBar `position="fixed"`. */}
      <Toolbar />

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Routes>
          <Route path="/super-admin" element={<SuperAdminConsole showSnackbar={showSnackbar} />} />
          <Route path="*" element={<Navigate to="/super-admin" replace />} />
        </Routes>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminShell;
